/**
 * Cambios de puesto entre dos cargas de la clasificación.
 *
 * La regla que importa: **si no hay carga anterior, no hay movimiento**. Abrir la pantalla
 * por primera vez no es que nadie haya subido ni bajado; es que todavía no se sabe. Animar
 * ahí sería inventarse una historia que no ha pasado, y además es el caso que más fácil se
 * rompe (comparar contra `undefined` y sacar `NaN`).
 *
 * Vive fuera del componente para poder fijar por test justo eso, sin montar React.
 */

export type Movimiento = 'sube' | 'baja' | 'igual' | 'nuevo';

export interface FilaConPuesto {
  user_id: string;
  rank: number;
}

/**
 * @param previos puestos de la carga anterior, o `null` si es la primera.
 * @returns un movimiento por usuario. Con `previos` a `null`, el mapa sale VACÍO: no hay
 *          nada que animar y quien lo consuma no tiene que acordarse de comprobarlo.
 */
export function calcularMovimientos(
  actuales: readonly FilaConPuesto[],
  previos: ReadonlyMap<string, number> | null,
): Map<string, Movimiento> {
  const movimientos = new Map<string, Movimiento>();
  if (previos === null || previos.size === 0) return movimientos;

  for (const fila of actuales) {
    const antes = previos.get(fila.user_id);
    if (antes === undefined) {
      // Alguien que no estaba: acaba de entrar en la liga. No ha "subido" desde ningún
      // sitio, así que tampoco se anima.
      movimientos.set(fila.user_id, 'nuevo');
      continue;
    }
    // El puesto va al revés que la intuición: el 1 es el mejor, así que bajar de número
    // es subir de posición.
    if (fila.rank < antes) movimientos.set(fila.user_id, 'sube');
    else if (fila.rank > antes) movimientos.set(fila.user_id, 'baja');
    else movimientos.set(fila.user_id, 'igual');
  }
  return movimientos;
}

/** Instantánea de puestos para comparar con la siguiente carga. */
export function instantanea(filas: readonly FilaConPuesto[]): Map<string, number> {
  return new Map(filas.map((f) => [f.user_id, f.rank]));
}

/** ¿Se ha movido alguien de verdad? Si no, no hay por qué animar nada. */
export function hayCambios(movimientos: ReadonlyMap<string, Movimiento>): boolean {
  for (const movimiento of movimientos.values()) {
    if (movimiento === 'sube' || movimiento === 'baja') return true;
  }
  return false;
}
